import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useWorld } from "../state/World";
import { IMAGES } from "../lib/tokens";
import Plate from "../components/Plate";
import { fmtAxvTrim } from "../lib/tokens";

/**
 * The Commons — endeavorum.org.
 *
 * Its own Portal on its own origin, sharing one substrate with the .apps. A
 * Skool member arriving via /api/seam/onboard-from-skool lands here at the
 * Teleport Pad (the compass rose in the founder's tavern render). On return
 * visits they resume where they last stood.
 *
 * Myst protocol (founder's rule): there are NO bridges from this island to
 * the ghosted islands you can see in the distance. Other Places are LOOKED
 * at, not walked to. Sealed origins are architecturally unreachable from
 * this page — no dead-end routes, no "coming soon" links. Curiosity is the
 * currency; that yearning is what makes the Q4 unlocks feel like a release.
 *
 * Sprint 0 = the shell: tavern plate, personal welcome, Pouch visible in the
 * HUD (rendered globally). Sprint 1 = the hotspots (Well, Sundial, Jukebox,
 * Fire Pit, Tavern Door) will layer over this same plate.
 */

const HOTSPOTS = [
  {
    key: "hearth",
    label: "The Hearth",
    hint: "Appreciation broadcasts. Tips to all.",
    // Positions are % of the plate — Sprint 1 will nudge these once the
    // proper tavern-with-compass-rose render replaces the interim plate.
    x: 42,
    y: 55,
  },
  {
    key: "well",
    label: "The Well Ring",
    hint: "Targeted tips to a specific member.",
    x: 22,
    y: 62,
  },
  {
    key: "sundial",
    label: "The Sundial",
    hint: "A daily prompt at your local sunset.",
    x: 62,
    y: 58,
  },
  {
    key: "jukebox",
    label: "The Jukebox",
    hint: "Drop AXV. Unlock a track. Keep it in your Sleeve.",
    x: 78,
    y: 44,
  },
  {
    key: "tavern",
    label: "The Tavern",
    hint: "Broadcasting Alcove. Show nights.",
    x: 50,
    y: 30,
  },
];

const arrivalGreeting = (name, pouchAxv) =>
  `Welcome, ${name}. Your Pouch is warm — ${pouchAxv} AXV. The Commons is quiet at this hour.`;

const returningGreeting = (name) =>
  `${name}. The fire is still going.`;

export const Commons = () => {
  const { me, mark } = useWorld();
  const [presenceCount, setPresenceCount] = useState(null);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [dismissedWelcome, setDismissedWelcome] = useState(false);
  const markedRef = useRef(false);

  const user = me?.user;
  const pouchSub = me?.pouch_sub ?? 0;
  const pouchAxv = useMemo(() => fmtAxvTrim(pouchSub), [pouchSub]);

  // First-time in the Commons (no engagement yet) gets the Teleport Pad
  // ceremony. Returning travellers get a warmer, shorter line.
  const commonsEngagement = (me?.engagements || []).find(
    (e) => e.realm === "commons"
  );
  const firstTime = !commonsEngagement;
  const greeting = firstTime
    ? arrivalGreeting(user?.display_name || "Endeavorer", pouchAxv)
    : returningGreeting(user?.display_name || "Endeavorer");

  // Stamp movement into the substrate — unified across every origin, so a
  // future MelzAI can ask "where did they leave off?" and get an answer.
  // Fire once per mount; the WorldProvider re-fetches /me on refresh.
  useEffect(() => {
    if (!user || markedRef.current) return;
    markedRef.current = true;
    mark("commons", "teleport-pad");
  }, [user, mark]);

  // Best-effort presence count. Silently no-ops until the backend exposes
  // /api/commons/presence — visible to Chase as a subtle badge, not an error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/commons/presence");
        if (!cancelled) setPresenceCount(data?.count ?? null);
      } catch (err) {
        // Endpoint not live yet — silent by design. Logged for observability.
        console.debug("[Commons] /commons/presence not available:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null; // WorldProvider handles the boot

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="commons-page">
      <Plate src={IMAGES.commonsFirepit} depth={10} />

      {/* Ambient vignette so text stays legible without dulling the render */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-52"
        style={{
          background:
            "linear-gradient(180deg, rgba(11,7,4,0.85) 0%, rgba(11,7,4,0.5) 55%, rgba(11,7,4,0) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40"
        style={{
          background:
            "linear-gradient(0deg, rgba(11,7,4,0.9) 0%, rgba(11,7,4,0) 100%)",
        }}
      />

      {/* The Teleport Pad greeting — first-time = ceremonial, return = warm */}
      {!dismissedWelcome && (
        <motion.div
          className="absolute inset-x-0 top-0 z-20 flex flex-col items-center gap-2 px-6 pt-10 md:pt-14"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.3 }}
          data-testid="commons-welcome-banner"
        >
          <p className="mono text-[10px] uppercase tracking-[0.42em] text-[#f2c877] md:text-[11px]">
            The Commons · Teleport Pad
          </p>
          <h1
            className="serif max-w-[720px] text-center text-[clamp(1.15rem,2.4vw,2rem)] font-light leading-tight text-[#fdf3d8]"
            data-testid="commons-welcome-copy"
          >
            {greeting}
          </h1>
          {firstTime && (
            <p className="serif mt-1 max-w-[560px] text-center text-[13px] italic leading-relaxed text-[#e2cba2]/80 md:text-[14px]">
              Look out across the water. Other islands are being conjured — you
              can see them from the patio, but there are no bridges yet.
            </p>
          )}
          <button
            type="button"
            onClick={() => setDismissedWelcome(true)}
            className="mono mt-2 text-[10px] uppercase tracking-[0.32em] text-white/40 transition hover:text-[#f2c877]"
            data-testid="commons-welcome-dismiss"
          >
            step off the pad
          </button>
        </motion.div>
      )}

      {/* Hotspots — Sprint 0 shows them as dim ambient dots that glow on hover
          with a tooltip, but click does nothing yet. Sprint 1 wires each to a
          real Practice. This exists now so the founder can walk the layout
          and reposition them against the proper tavern render. */}
      <div
        className="pointer-events-none absolute inset-0 z-15"
        aria-hidden={dismissedWelcome ? "false" : "true"}
      >
        {HOTSPOTS.map((h) => (
          <motion.button
            key={h.key}
            type="button"
            className="pointer-events-auto absolute grid h-8 w-8 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[#f2c877]/30 bg-[#0b0704]/40 backdrop-blur-sm transition hover:border-[#f2c877]/90 hover:bg-[#c9a227]/25"
            style={{ left: `${h.x}%`, top: `${h.y}%` }}
            onMouseEnter={() => setHoveredHotspot(h.key)}
            onMouseLeave={() => setHoveredHotspot((k) => (k === h.key ? null : k))}
            onFocus={() => setHoveredHotspot(h.key)}
            onBlur={() => setHoveredHotspot((k) => (k === h.key ? null : k))}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: dismissedWelcome ? 0.75 : 0.35,
              scale: 1,
            }}
            transition={{ duration: 0.9, delay: 1.6 + HOTSPOTS.indexOf(h) * 0.15 }}
            data-testid={`commons-hotspot-${h.key}`}
            aria-label={h.label}
          >
            <span
              className="h-1.5 w-1.5 rounded-full bg-[#f2c877]"
              style={{
                boxShadow: "0 0 12px 3px rgba(242,200,119,0.55)",
              }}
            />
          </motion.button>
        ))}

        {/* Tooltip for the currently hovered hotspot */}
        {hoveredHotspot && dismissedWelcome && (
          <motion.div
            className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[220%] rounded-lg border border-[#f2c877]/25 bg-[#0b0704]/95 px-3 py-2 shadow-lg backdrop-blur-md"
            style={{
              left: `${HOTSPOTS.find((h) => h.key === hoveredHotspot).x}%`,
              top: `${HOTSPOTS.find((h) => h.key === hoveredHotspot).y}%`,
            }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="commons-hotspot-tooltip"
          >
            <p className="mono mb-0.5 text-[9px] uppercase tracking-[0.32em] text-[#f2c877]">
              {HOTSPOTS.find((h) => h.key === hoveredHotspot).label}
            </p>
            <p className="serif text-[12px] leading-snug text-[#fdf3d8]/85">
              {HOTSPOTS.find((h) => h.key === hoveredHotspot).hint}
            </p>
            <p className="mono mt-1 text-[8px] uppercase tracking-[0.28em] text-white/40">
              Sprint 1 · lighting up soon
            </p>
          </motion.div>
        )}
      </div>

      {/* Presence badge — subtle, bottom-left. Zero-count is honest, not a bug. */}
      <motion.div
        className="absolute bottom-6 left-6 z-20 flex items-center gap-2 rounded-full border border-white/10 bg-[#0b0704]/70 px-3 py-1.5 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: dismissedWelcome ? 1 : 0.4 }}
        transition={{ duration: 0.8 }}
        data-testid="commons-presence"
      >
        <span
          className="h-1.5 w-1.5 rounded-full bg-[#c9a227]"
          style={{ boxShadow: "0 0 8px 2px rgba(201,162,39,0.6)" }}
        />
        <p className="mono text-[10px] uppercase tracking-[0.32em] text-white/60">
          {presenceCount === null || presenceCount <= 1
            ? "You are the only Endeavorer here"
            : `${presenceCount} Endeavorers on the patio`}
        </p>
      </motion.div>
    </div>
  );
};

export default Commons;
